// pages/admin/complaints/index.jsx
import { useNavigate, useSearchParams } from "react-router-dom";
import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { Suspense, useEffect, useMemo, useState } from "react";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { useQuery } from "@tanstack/react-query";
import { lazily } from "react-lazily";
import { complaintsService, COMPLAINT_STATUSES, COMPLAINT_CATEGORIES } from "@/api/complaints";
import { FiMessageSquare, FiSearch } from "react-icons/fi";
import { useTranslation } from "@/hooks/useTranslation";

const { AdminComplaintsList } = lazily(() =>
    import("@/pages/admin/complaints/AdminComplaintsList")
);
const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

const PAGE_SIZE = 10;
const BASE_PATH = "/admin/complaints";

const AdminComplaints = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const currentPage = parseInt(params.get("page")) || 0;
    const statusFilter = params.get("status") || "";
    const categoryFilter = params.get("category") || "";
    const searchFilter = params.get("search") || "";

    const [searchInput, setSearchInput] = useState(searchFilter);

    useEffect(() => {
        setSearchInput(searchFilter);
    }, [searchFilter]);

    const {
        isPending: loading,
        isFetching,
        data,
        error,
        refetch,
    } = useQuery({
        queryKey: ["admin-complaints", currentPage, statusFilter, categoryFilter, searchFilter],
        queryFn: () =>
            complaintsService.getComplaints(currentPage, PAGE_SIZE, {
                status: statusFilter,
                category: categoryFilter,
                search: searchFilter,
            }),
        retry: false,
        refetchOnWindowFocus: false,
    });

    const complaints = data?.result || [];
    const pagination = {
        totalItems: data?.totalItems || 0,
        totalPages: data?.totalPages || 1,
    };

    // Empty values are removed from the URL
    const updateParams = (changes, resetPage = true) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(changes).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) next.delete(key);
            else next.set(key, value);
        });
        if (resetPage) next.delete("page");
        const qs = next.toString();
        navigate(qs ? `${BASE_PATH}?${qs}` : BASE_PATH);
    };

    const goToPage = (page) => updateParams({ page: page || "" }, false);

    const applySearch = () => updateParams({ search: searchInput.trim() });

    const statusOptions = [
        { value: "", label: t("complaints.statusAll") },
        ...COMPLAINT_STATUSES.map((s) => ({ value: s, label: t(`complaints.status.${s}`) })),
    ];

    const categoryOptions = [
        { value: "", label: t("complaints.categoryAll") },
        ...COMPLAINT_CATEGORIES.map((c) => ({ value: c, label: t(`complaints.category.${c}`) })),
    ];

    const hasFilters = statusFilter !== "" || categoryFilter !== "" || searchFilter !== "";

    const pageNumbers = useMemo(() => {
        const count = Math.min(5, pagination.totalPages);
        const start = Math.max(0, Math.min(currentPage - 2, pagination.totalPages - count));
        return Array.from({ length: count }, (_, i) => start + i);
    }, [currentPage, pagination.totalPages]);

    const loadingItem = useMemo(
        () => (
            <div className="size-full z-20 absolute flex items-center justify-center grow">
                <TaleLoadingFallback />
            </div>
        ),
        []
    );

    const fieldClass =
        "w-full sm:w-auto px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

    return (
        <TaleDashbaordMainCard className={"flex flex-col gap-6 min-w-0"}>
            {/* Header */}
            <div className="pb-4 border-b border-gray-200">
                <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex flex-col min-w-0">
                        <h2 className="!text-2xl font-bold !m-0">{t("complaints.listTitle")}</h2>
                        <p className="text-gray-600 !m-0">{t("complaints.listSubtitle")}</p>
                    </div>
                    <TaleButton
                        role="normal"
                        variant="outlined"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="self-start sm:self-auto flex-shrink-0"
                    >
                        <i className={`fas fa-sync-alt mr-2 rtl:mr-0 rtl:ml-2 ${isFetching ? "fa-spin" : ""}`}></i>
                        {t("complaints.refresh")}
                    </TaleButton>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                    <select
                        className={fieldClass}
                        value={statusFilter}
                        onChange={(e) => updateParams({ status: e.target.value })}
                    >
                        {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <select
                        className={fieldClass}
                        value={categoryFilter}
                        onChange={(e) => updateParams({ category: e.target.value })}
                    >
                        {categoryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <div className="relative w-full sm:w-64">
                        <FiSearch className="absolute top-1/2 -translate-y-1/2 left-3 rtl:left-auto rtl:right-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && applySearch()}
                            onBlur={() => searchInput.trim() !== searchFilter && applySearch()}
                            placeholder={t("complaints.searchPlaceholder")}
                            className="w-full pl-9 rtl:pl-4 rtl:pr-9 pr-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div className="lg:ml-auto rtl:lg:ml-0 rtl:lg:mr-auto text-sm text-gray-600">
                    {t("complaints.foundCount", { count: pagination.totalItems })}
                </div>
            </div>

            {/* List */}
            <div
                className={`flex flex-col relative grow min-w-0 ${
                    !complaints.length && !loading ? "items-center justify-center gap-4 min-h-[400px]" : "gap-4"
                }`}
            >
                {loading && loadingItem}
                {error && !loading ? (
                    <div className="size-full z-20 absolute flex items-center justify-center grow">
                        <InternalServerError />
                    </div>
                ) : null}
                <Suspense fallback={loadingItem}>
                    {loading || error ? null : !complaints.length ? (
                        <div className="text-center py-12 px-4">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 mb-6">
                                <FiMessageSquare className="w-10 h-10 text-blue-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {statusFilter === "OPEN" && !categoryFilter && !searchFilter
                                    ? t("complaints.noOpenComplaints")
                                    : t("complaints.noComplaints")}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {hasFilters ? t("complaints.noMatchFilters") : t("complaints.emptyHint")}
                            </p>
                            {hasFilters && (
                                <button
                                    onClick={() => updateParams({ status: "", category: "", search: "" })}
                                    className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    {t("complaints.viewAll")}
                                </button>
                            )}
                        </div>
                    ) : (
                        <AdminComplaintsList
                            complaints={complaints}
                            onViewDetails={(complaint) => navigate(`${BASE_PATH}/${complaint.id}`)}
                        />
                    )}
                </Suspense>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-4 pt-6 border-t border-gray-200">
                    <button
                        className="px-3 sm:px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                        disabled={currentPage <= 0}
                        onClick={() => goToPage(currentPage - 1)}
                    >
                        <i className="fas fa-chevron-left rtl:-scale-x-100"></i>
                        <span className="hidden sm:inline">{t("complaints.previous")}</span>
                    </button>

                    <div className="flex items-center gap-2">
                        {pageNumbers.map((pageNum) => (
                            <button
                                key={pageNum}
                                className={`w-10 h-10 flex items-center justify-center border rounded-lg transition-all ${
                                    currentPage === pageNum
                                        ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-500 shadow-sm"
                                        : "hover:bg-gray-50 hover:border-gray-300"
                                }`}
                                onClick={() => goToPage(pageNum)}
                            >
                                {pageNum + 1}
                            </button>
                        ))}
                    </div>

                    <button
                        className="px-3 sm:px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                        disabled={currentPage >= pagination.totalPages - 1}
                        onClick={() => goToPage(currentPage + 1)}
                    >
                        <span className="hidden sm:inline">{t("complaints.next")}</span>
                        <i className="fas fa-chevron-right rtl:-scale-x-100"></i>
                    </button>
                </div>
            )}
        </TaleDashbaordMainCard>
    );
};

export default AdminComplaints;